import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, subDays, isAfter, isBefore, startOfDay } from 'date-fns';
import api from '../../services/api';

interface BoletoDDA {
  codigoBarras: string;
  linhaDigitavel: string;
  valorBoleto: number;
  valorDesconto: number;
  valorMultaMora: number;
  dataVencimento: string;
  dataEmissao?: string;
  nomeBeneficiario: string;
  numeroCpfCnpjBeneficiario: string;
  nomePagador: string;
  numeroCpfCnpjPagador: string;
  situacao: number;
  situacaoDescricao?: string;
}

interface ConsultaParams {
  dataInicial: Date | null;
  dataFinal: Date | null;
  situacao: number;
  tipoData: number;
}

const situacaoOptions = [
  { value: 1, label: 'Em Aberto', color: 'warning' as const, icon: <ScheduleIcon /> },
  { value: 2, label: 'Agendado', color: 'info' as const, icon: <CalendarIcon /> },
  { value: 3, label: 'Liquidado', color: 'success' as const, icon: <CheckIcon /> },
  { value: 4, label: 'Baixado', color: 'default' as const, icon: <CancelIcon /> },
];

const tipoDataOptions = [
  { value: 1, label: 'Vencimento' },
  { value: 2, label: 'Emissão' },
  { value: 3, label: 'Inclusão' },
];

export default function PagamentosDDAPage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [boletos, setBoletos] = useState<BoletoDDA[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [selectedBoleto, setSelectedBoleto] = useState<BoletoDDA | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [params, setParams] = useState<ConsultaParams>({
    dataInicial: subDays(new Date(), 30),
    dataFinal: new Date(),
    situacao: 1,
    tipoData: 1,
  });

  const handleSearch = useCallback(async () => {
    if (!params.dataInicial || !params.dataFinal) {
      setError('Datas inicial e final são obrigatórias');
      return;
    }

    setLoading(true);
    setError(null);
    setServiceError(null);
    setSearched(true);

    try {
      const response = await api.get('/pagamentos/dda', {
        params: {
          dataInicial: format(params.dataInicial, 'yyyy-MM-dd'),
          dataFinal: format(params.dataFinal, 'yyyy-MM-dd'),
          situacao: params.situacao,
          tipoData: params.tipoData,
        },
      });

      if (response.success) {
        const boletosData = response.data?.resultado || response.data || [];
        setBoletos(Array.isArray(boletosData) ? boletosData : []);
      } else {
        const errorMsg = response.error?.message || 'Erro ao consultar boletos DDA';
        // Check if it's a service configuration error
        if (errorMsg.includes('não está ativo') || errorMsg.includes('Invalid scopes')) {
          setServiceError(errorMsg);
        } else {
          setError(errorMsg);
        }
      }
    } catch (err: any) {
      const errorMsg = err.error?.message || err.message || 'Erro ao consultar boletos DDA';
      if (errorMsg.includes('não está ativo') || errorMsg.includes('Invalid scopes')) {
        setServiceError(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [params]);

  const handleOpenDetail = (boleto: BoletoDDA) => {
    setSelectedBoleto(boleto);
    setDetailDialogOpen(true);
  };

  const handleCopyLinhaDigitavel = async () => {
    if (!selectedBoleto) return;
    const texto = selectedBoleto.linhaDigitavel || selectedBoleto.codigoBarras;
    await navigator.clipboard.writeText(texto);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handlePay = async () => {
    if (!selectedBoleto) return;

    setPaymentLoading(true);
    setError(null);

    try {
      const consultaResponse = await api.get(`/pagamentos/boletos/${selectedBoleto.codigoBarras}`);

      if (!consultaResponse.success) {
        setError(consultaResponse.error?.message || 'Erro ao consultar boleto para pagamento');
        return;
      }

      const consultaData = consultaResponse.data;

      const pagamentoResponse = await api.post(`/pagamentos/boletos/${selectedBoleto.codigoBarras}/pagar`, {
        identificadorConsulta: consultaData.identificadorConsulta,
        valorBoleto: selectedBoleto.valorBoleto,
        valorDescontoAbatimento: selectedBoleto.valorDesconto || 0,
        valorMultaMora: selectedBoleto.valorMultaMora || 0,
        aceitaValorDivergente: false,
      });

      if (pagamentoResponse.success) {
        setDetailDialogOpen(false);
        handleSearch();
        alert(`Pagamento realizado com sucesso!\nAutenticação: ${pagamentoResponse.data?.numeroAutenticacao || 'N/A'}`);
      } else {
        setError(pagamentoResponse.error?.message || 'Erro ao realizar pagamento');
      }
    } catch (err: any) {
      setError(err.error?.message || err.message || 'Erro ao realizar pagamento');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatCpfCnpj = (value: string) => {
    if (!value) return '-';
    const clean = value.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const getSituacaoChip = (situacao: number) => {
    const option = situacaoOptions.find(o => o.value === situacao);
    return option ? (
      <Chip size="small" label={option.label} color={option.color} icon={option.icon} />
    ) : (
      <Chip size="small" label={`Situação ${situacao}`} />
    );
  };

  const isVencido = (dataVencimento: string) => {
    if (!dataVencimento) return false;
    try {
      const vencimento = new Date(dataVencimento);
      return isBefore(startOfDay(vencimento), startOfDay(new Date()));
    } catch {
      return false;
    }
  };

  // Calculate summary
  const summary = {
    total: boletos.length,
    valorTotal: boletos.reduce((acc, b) => acc + (b.valorBoleto || 0), 0),
    vencidos: boletos.filter(b => b.situacao === 1 && isVencido(b.dataVencimento)).length,
    aVencer: boletos.filter(b => b.situacao === 1 && !isVencido(b.dataVencimento)).length,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BankIcon color="primary" />
              Boletos DDA
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Débito Direto Autorizado - Boletos registrados em seu CNPJ/CPF
            </Typography>
          </Box>
        </Box>

        {/* Service Error Alert */}
        {serviceError && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            icon={<WarningIcon />}
          >
            <AlertTitle>Serviço DDA não disponível</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {serviceError}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Para utilizar o DDA, entre em contato com sua cooperativa Sicoob e solicite a ativação
              do serviço de Débito Direto Autorizado para sua conta.
            </Typography>
          </Alert>
        )}

        {/* Info Alert */}
        {!serviceError && (
          <Alert
            severity="info"
            sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.08) }}
            icon={<InfoIcon />}
          >
            <AlertTitle>O que é DDA?</AlertTitle>
            O <strong>Débito Direto Autorizado</strong> permite visualizar eletronicamente os boletos
            emitidos em seu CNPJ/CPF antes mesmo de recebê-los fisicamente. Consulte e pague seus
            boletos de forma segura e centralizada.
          </Alert>
        )}

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon fontSize="small" color="action" />
            Filtros de Consulta
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Data Inicial"
                value={params.dataInicial}
                onChange={(date) => setParams({ ...params, dataInicial: date })}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Data Final"
                value={params.dataFinal}
                onChange={(date) => setParams({ ...params, dataFinal: date })}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Situação"
                value={params.situacao}
                onChange={(e) => setParams({ ...params, situacao: Number(e.target.value) })}
                SelectProps={{ native: true }}
              >
                {situacaoOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo de Data"
                value={params.tipoData}
                onChange={(e) => setParams({ ...params, tipoData: Number(e.target.value) })}
                SelectProps={{ native: true }}
              >
                {tipoDataOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={loading || !!serviceError}
                sx={{ height: '40px' }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards - Only show when there are boletos */}
        {searched && boletos.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ReceiptIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total de Boletos
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {summary.total}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TrendingUpIcon color="success" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Valor Total
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">
                        {formatCurrency(summary.valorTotal)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningIcon color="error" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Vencidos
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {summary.vencidos}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ScheduleIcon color="warning" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        A Vencer
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="warning.main">
                        {summary.aVencer}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabela de Boletos */}
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Boletos Encontrados
            </Typography>
            <Tooltip title="Atualizar lista">
              <span>
                <IconButton onClick={handleSearch} disabled={loading || !!serviceError}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Vencimento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Beneficiário</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>CNPJ/CPF</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Situação</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {boletos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : serviceError ? (
                          <>
                            <WarningIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                            <Typography color="text.secondary">
                              Serviço DDA não disponível para esta conta
                            </Typography>
                          </>
                        ) : searched ? (
                          <>
                            <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography color="text.secondary">
                              Nenhum boleto encontrado no período selecionado
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Tente alterar os filtros de busca
                            </Typography>
                          </>
                        ) : (
                          <>
                            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography color="text.secondary">
                              Use os filtros acima para buscar seus boletos DDA
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  boletos.map((boleto, index) => {
                    const vencido = isVencido(boleto.dataVencimento) && boleto.situacao === 1;
                    return (
                      <TableRow
                        key={boleto.codigoBarras || index}
                        hover
                        sx={vencido ? { bgcolor: alpha(theme.palette.error.main, 0.05) } : {}}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color={vencido ? 'error.main' : 'text.primary'}
                          >
                            {formatDate(boleto.dataVencimento)}
                          </Typography>
                          {vencido && (
                            <Chip
                              label="Vencido"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontWeight: 500 }}>
                            {boleto.nomeBeneficiario || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {formatCpfCnpj(boleto.numeroCpfCnpjBeneficiario)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="primary.main">
                            {formatCurrency(boleto.valorBoleto)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {getSituacaoChip(boleto.situacao)}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Ver detalhes">
                              <IconButton size="small" onClick={() => handleOpenDetail(boleto)}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {boleto.situacao === 1 && (
                              <Tooltip title="Pagar boleto">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenDetail(boleto)}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                  }}
                                >
                                  <PaymentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Dialog de Detalhes */}
        <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon />
              Detalhes do Boleto DDA
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedBoleto && (
              <Box>
                {/* Valor Card */}
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: 'primary.main'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h3" color="primary.main" fontWeight={700}>
                      {formatCurrency(selectedBoleto.valorBoleto)}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
                      <Chip
                        label={`Vencimento: ${formatDate(selectedBoleto.dataVencimento)}`}
                        variant="outlined"
                        color={isVencido(selectedBoleto.dataVencimento) ? 'error' : 'default'}
                      />
                      {getSituacaoChip(selectedBoleto.situacao)}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Beneficiário */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" color="text.secondary">
                    Beneficiário
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedBoleto.nomeBeneficiario || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatCpfCnpj(selectedBoleto.numeroCpfCnpjBeneficiario)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pagador */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" color="text.secondary">
                    Pagador
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedBoleto.nomePagador || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatCpfCnpj(selectedBoleto.numeroCpfCnpjPagador)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Valores */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">
                      Desconto
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {formatCurrency(selectedBoleto.valorDesconto)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">
                      Multa/Mora
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="error.main">
                      {formatCurrency(selectedBoleto.valorMultaMora)}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Linha Digitável */}
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Linha Digitável
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        flex: 1,
                      }}
                    >
                      {selectedBoleto.linhaDigitavel || selectedBoleto.codigoBarras}
                    </Typography>
                    <Tooltip title={copySuccess ? 'Copiado!' : 'Copiar'}>
                      <IconButton size="small" onClick={handleCopyLinhaDigitavel} color={copySuccess ? 'success' : 'default'}>
                        {copySuccess ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </Paper>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDetailDialogOpen(false)} variant="outlined">
              Fechar
            </Button>
            {selectedBoleto?.situacao === 1 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={paymentLoading ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
                onClick={handlePay}
                disabled={paymentLoading}
                size="large"
              >
                Pagar Boleto
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
